/**
 * @file products.ts
 *
 * Product API for the shop admin, wired to the real commerce-engine `/v1` catalog
 * API: {@link fetchProducts} (list) and {@link fetchProductById} (single) on the read
 * path, {@link createProduct} / {@link updateProduct} / {@link deleteProduct} on the
 * write path. Each goes through {@link v1Get} / {@link v1Mutate} and maps between the
 * `/v1` shapes and the design-reviewed `Shop*` shapes the screens consume, so the
 * store and screens stay untouched. The product form's category picker reads from
 * `./categories` (`fetchProductCategories`), which owns the category mapping.
 */

import { v1Get, v1Mutate } from './v1-request';
import { fromMoney, parseDecimal, toMoney } from './money';
import { getShopCurrency } from './shop';
import type { WcResult } from './types';
import { WooProductType } from './shop-types';
import type {
  FetchProductsParams,
  ShopOptionValue,
  ShopProduct,
  ShopProductPayload,
  ShopProductVariant,
  ShopStockSummary,
} from './shop-types';
import type {
  BatchUpdateVariantsRequestV1,
  CreateProductRequestV1,
  MoneyV1,
  ProductOptionInputV1,
  ProductOptionV1,
  ProductResponseV1,
  ProductStatusV1,
  ProductSummaryResponseV1,
  ProductVariantV1,
  StockSummaryV1,
  UpdateProductRequestV1,
  VariantBatchItemV1,
  VariantOverrideInputV1,
} from './v1-types';

/** Grams per kilogram (the weight field is entered in kg; the wire is grams). */
const GRAMS_PER_KG = 1000;

/**
 * Variation-axis fields shared by the loaded product and the form payload.
 * Only `name` + `options` matter for the CE `options` wire shape.
 */
type AttributeAxes = ReadonlyArray<{ name: string; options: ShopOptionValue[] }>;

/**
 * Maps admin attributes → CE `options` (`{ name, values }`). A value that carries its
 * engine value id (a kept/renamed value read from the product) is emitted as
 * `{ id, value }` so the engine renames it in place and preserves its variant; a value
 * with no id (one the merchant just typed) is emitted as a bare label so the engine
 * inserts it — matching today's wire exactly for new values.
 *
 * @param attributes - Axes from the form payload or the product loaded into the form.
 *
 * @returns The wire-shaped option list.
 */
function toVariationAxes(attributes: AttributeAxes): ProductOptionInputV1[] {
  return attributes.map(({ name, options }) => ({
    name,
    values: options.map((o) => (o.id ? { id: o.id, value: o.value } : o.value)),
  }));
}

/**
 * Whether the merchant changed the variation matrix (including reorder).
 * Compared as JSON so order of axes and values is significant.
 *
 * @param next - Axes from the form payload.
 * @param previous - Axes from the product loaded into the form (pre-edit).
 *
 * @returns `true` when the matrices differ.
 */
function variationAxesChanged(
  next: ProductOptionInputV1[],
  previous: ProductOptionInputV1[],
): boolean {
  return JSON.stringify(next) !== JSON.stringify(previous);
}

/** Default products per page for the list view. */
export const PRODUCTS_PER_PAGE = 50;

/** `/v1` lifecycle status → the WooCommerce-style status the screens use. */
const V1_TO_SHOP_STATUS: Record<ProductStatusV1, string> = {
  active: 'publish',
  draft: 'draft',
  hidden: 'private',
};

/** WooCommerce-style status (form/filter) → the `/v1` lifecycle status. */
const SHOP_TO_V1_STATUS: Record<string, ProductStatusV1> = {
  publish: 'active',
  draft: 'draft',
  private: 'hidden',
};

/** Derives the WooCommerce-style stock status from the raw admin inventory fields. */
function stockStatusOf(trackInventory: boolean, inventoryQuantity: number): string {
  return !trackInventory || inventoryQuantity > 0 ? 'instock' : 'outofstock';
}

/** The stock-status key for a product where SOME (not all) enabled variants are out. */
export const PARTIAL_STOCK_STATUS = 'partialstock';

/**
 * Derives the list/drawer stock status from the admin `stockSummary` rollup (over
 * enabled variants). A product is in stock when any enabled variant is in stock
 * (`inStockCount > 0`); when some are in and some are out it is a distinct
 * {@link PARTIAL_STOCK_STATUS} ("some variants out of stock", rendered amber); only a
 * product with no in-stock enabled variant is plain "outofstock". The old
 * {@link stockStatusOf} is kept for simple products / summaries without the aggregate.
 *
 * @param summary - The `/v1` sellable-stock rollup.
 *
 * @returns `"instock"`, `"partialstock"`, or `"outofstock"`.
 */
export function stockStatusFromSummary(summary: StockSummaryV1): string {
  if (summary.inStockCount > 0) {
    return summary.outOfStockCount > 0 ? PARTIAL_STOCK_STATUS : 'instock';
  }
  return 'outofstock';
}

/** Maps the `/v1` stock summary to the design-shape {@link ShopStockSummary} (1:1). */
function mapStockSummary(summary: StockSummaryV1): ShopStockSummary {
  return {
    lowestQuantity: summary.lowestQuantity,
    trackedQuantitySum: summary.trackedQuantitySum,
    outOfStockCount: summary.outOfStockCount,
    inStockCount: summary.inStockCount,
    trackedCount: summary.trackedCount,
  };
}

/**
 * Derives the sellable-stock rollup from a full product's variant rows — the same numbers
 * the list summary's `stockSummary` reports, so the detail drawer's stock badge cannot
 * diverge from the list row it was opened from (the full `/v1` resource carries no
 * `stockSummary` of its own). Computed over ENABLED variants only, matching the engine's
 * rollup; an untracked variant counts as in stock and contributes no quantity.
 *
 * @param variants - The full product's `/v1` variant rows.
 *
 * @returns The rollup in the design shape.
 */
export function stockSummaryFromVariants(variants: ProductVariantV1[]): ShopStockSummary {
  const enabled = variants.filter((v) => v.enabled);
  const tracked = enabled.filter((v) => v.trackInventory);
  const outOfStockCount = tracked.filter((v) => v.inventoryQuantity <= 0).length;
  return {
    lowestQuantity: tracked.length
      ? Math.min(...tracked.map((v) => v.inventoryQuantity))
      : null,
    trackedQuantitySum: tracked.reduce((sum, v) => sum + v.inventoryQuantity, 0),
    outOfStockCount,
    inStockCount: enabled.length - outOfStockCount,
    trackedCount: tracked.length,
  };
}

/** Maps the `/v1` price range to display strings, or `null` when the wire omitted it. */
function mapPriceRange(
  range: { min: MoneyV1; max: MoneyV1 } | undefined,
): { min: string; max: string } | null {
  return range ? { min: fromMoney(range.min), max: fromMoney(range.max) } : null;
}

/**
 * Resolves a variant's option-value id pairs to human `{ name, value }` selectors by
 * joining against the product's `options[]`. Reads give ids; the table displays labels
 * and writes send `{ name, value }` selectors (there is no id-write path). Ordered by the
 * option's `position`. An id that no longer resolves (a stale pin) is skipped.
 *
 * @param variant - The `/v1` admin variant, carrying `optionValues` id pairs.
 * @param options - The product's `options[]`, carrying value ids and labels.
 *
 * @returns The combination selectors, ordered by option position.
 */
export function resolveVariantOptions(
  variant: ProductVariantV1,
  options: ProductOptionV1[],
): { name: string; value: string }[] {
  const byOptionId = new Map(options.map((o) => [o.id, o]));
  return variant.optionValues
    .flatMap(({ optionId, optionValueId }) => {
      const option = byOptionId.get(optionId);
      const value = option?.values.find((v) => v.id === optionValueId);
      return option && value
        ? [{ name: option.name, value: value.value, position: option.position }]
        : [];
    })
    .sort((a, b) => a.position - b.position)
    .map(({ name, value }) => ({ name, value }));
}

/** Joins combination selectors into a display label, e.g. "M / Red". */
function variantLabel(selectors: { name: string; value: string }[]): string {
  return selectors.map((s) => s.value).join(' / ');
}

/**
 * Maps a full `/v1` admin variant to the {@link ShopProductVariant} the form's
 * per-variant table consumes. Money/weight are the same display strings the rest of
 * {@link ShopProduct} uses; `stock_quantity` is `null` when the variant is untracked.
 *
 * @param variant - The `/v1` admin variant.
 * @param options - The product's `options[]` (for id → label resolution).
 *
 * @returns The variant in the design-reviewed shape.
 */
function toShopProductVariant(
  variant: ProductVariantV1,
  options: ProductOptionV1[],
): ShopProductVariant {
  const selectors = resolveVariantOptions(variant, options);
  return {
    id: variant.id,
    options: selectors,
    label: variantLabel(selectors),
    regular_price: fromMoney(variant.price),
    sale_price: variant.salePrice ? fromMoney(variant.salePrice) : '',
    sku: variant.sku ?? '',
    weight: variant.weightGrams != null ? gramsToKg(variant.weightGrams) : '',
    manage_stock: variant.trackInventory,
    stock_quantity: variant.trackInventory ? variant.inventoryQuantity : null,
    image: variant.image
      ? { id: variant.image.id, src: variant.image.url, alt: variant.image.alt }
      : null,
    enabled: variant.enabled,
  };
}

/** Canonical grams → the kilogram string the weight field edits. */
function gramsToKg(grams: number): string {
  return (grams / 1000).toString();
}

/** Slug for a variation axis (option names are unique, so this id is too). */
function optionSlug(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, '-');
}

/**
 * Maps a full `/v1` admin product to the {@link ShopProduct} the screens consume.
 *
 * @param p - The `/v1` product resource (admin audience).
 *
 * @returns The product in the design-reviewed `Shop*` shape.
 */
export function toShopProduct(p: ProductResponseV1): ShopProduct {
  const isVariable = p.options.length > 0;
  // The SELLABLE variant set. The admin wire deliberately returns retired variants too
  // (`ProductVariantV1.enabled`) — only its DERIVED fields (representative, priceRange,
  // stockSummary, variantCount) are filtered — so the filter has to happen here. Without it a
  // variant retired in an EARLIER session is indistinguishable from one marked in THIS one:
  // the table renders it dimmed with an Undo button whose write the engine rejects (retiring
  // is one-way, 422), and `suppressedCombinations` — which asks for combinations absent from
  // the loaded rows — can never fire.
  const sellableVariants = p.variants.filter((v) => v.enabled);
  // The full resource carries no `stockSummary`, so derive the same rollup from the
  // variant rows for a variable product — the drawer's badge must agree with the list
  // row's. A simple product keeps the raw product-level fields (they ARE its variant);
  // a variable payload without rows (fixtures) falls back the same way.
  const derivedSummary =
    isVariable && sellableVariants.length > 0 ? stockSummaryFromVariants(sellableVariants) : null;
  return {
    id: p.id,
    name: p.name,
    type: isVariable ? WooProductType.Variable : WooProductType.Simple,
    status: V1_TO_SHOP_STATUS[p.status] ?? p.status,
    price: fromMoney(p.price),
    regular_price: fromMoney(p.price),
    sale_price: p.salePrice ? fromMoney(p.salePrice) : '',
    sku: p.sku ?? '',
    stock_status: derivedSummary
      ? stockStatusFromSummary(derivedSummary)
      : stockStatusOf(p.trackInventory, p.inventoryQuantity),
    stock_quantity: p.trackInventory ? p.inventoryQuantity : null,
    total_sales: 0,
    categories: p.categories.map((c) => ({ id: c.id, name: c.name })),
    images: p.images.map((img) => ({ id: img.id, src: img.url, alt: img.alt })),
    description: p.description ?? '',
    short_description: p.shortDescription ?? '',
    date_created: p.createdAt,
    updated_at: p.updatedAt,
    permalink: '',
    virtual: p.productType === 'digital',
    weight: p.weightGrams != null ? gramsToKg(p.weightGrams) : '',
    // WB "only allow one per order" ↔ CE maxPerOrder === 1 (null/other = unlimited).
    sold_individually: p.maxPerOrder === 1,
    downloads: p.download
      ? p.download.files.map((f) => ({ id: f.mediaId, name: f.name ?? '', file: '' }))
      : [],
    attributes: p.options.map((o) => ({
      id: o.id,
      name: o.name,
      slug: optionSlug(o.name),
      position: o.position,
      visible: true,
      variation: true,
      // Retain each value's engine id so an edit + save renames it in place (the id
      // rides `toVariationAxes` → the wire) rather than folding to a remove + insert.
      options: o.values.map((v) => ({ id: v.id, value: v.value })),
    })),
    cross_sell_ids: p.relatedProductIds,
    // Sellable rows only (see `sellableVariants` above) — resolved to labelled rows for
    // the form's table.
    //
    // KNOWN, ACCEPTED EXPOSURE: because retired variants are invisible here, the form
    // cannot restate them on an axes save — so an ID-LESS relabel of an option value
    // (a remove + re-type gesture, still expressible) mints a NEW value id, and the
    // engine soft-deletes the retired variant's combination and mints a fresh, ENABLED
    // one at the seed price. Id-PRESERVING renames (the pill editor's gesture) are safe:
    // the engine's reconcile keeps a retired survivor retired. WSB defends by restating
    // retired combinations (`retireVariantKeys`); this admin surface accepts the edge —
    // revisit if it ever serves production merchants.
    variants: sellableVariants.map((v) => toShopProductVariant(v, p.options)),
    // Derived from the variant rows (the full resource carries no wire `stockSummary`),
    // so the drawer badge and count match the list row. `null` for a simple product.
    stock_summary: derivedSummary,
    // Sellable count, matching the wire's own `variantCount` (which filters the same way).
    variant_count: sellableVariants.length,
    price_range: mapPriceRange(p.priceRange),
  };
}

/**
 * Maps a `/v1` admin product summary (list row) to a {@link ShopProduct}. The
 * summary is lighter than the full resource, so the fields it omits (descriptions,
 * weight, attributes, downloads) default to empty; the detail drawer and edit form
 * fetch the full product by id when those are needed. Categories ARE carried on the
 * summary (the list renders them), mapped the same way as the full resource.
 *
 * @param s - The `/v1` product summary (admin audience).
 *
 * @returns The product in the design-reviewed `Shop*` shape (list fields only).
 */
export function toShopProductSummary(s: ProductSummaryResponseV1): ShopProduct {
  return {
    id: s.id,
    name: s.name,
    type: s.optionCount > 0 ? WooProductType.Variable : WooProductType.Simple,
    status: V1_TO_SHOP_STATUS[s.status] ?? s.status,
    price: fromMoney(s.price),
    regular_price: fromMoney(s.price),
    sale_price: s.salePrice ? fromMoney(s.salePrice) : '',
    sku: s.sku ?? '',
    // Prefer the sellable-stock rollup (over enabled variants) when the admin summary
    // carries it; fall back to the raw product-level fields for a summary without it.
    stock_status: s.stockSummary
      ? stockStatusFromSummary(s.stockSummary)
      : stockStatusOf(s.trackInventory, s.inventoryQuantity),
    stock_quantity: s.trackInventory ? s.inventoryQuantity : null,
    total_sales: 0,
    categories: s.categories.map((c) => ({ id: c.id, name: c.name })),
    images: s.image ? [{ id: s.image.id, src: s.image.url, alt: s.image.alt }] : [],
    description: '',
    short_description: '',
    date_created: s.createdAt,
    permalink: '',
    virtual: s.productType === 'digital',
    weight: '',
    // List summaries omit maxPerOrder; the edit form loads the full product by id.
    sold_individually: false,
    downloads: [],
    attributes: [],
    cross_sell_ids: [],
    // The list row uses the aggregate rollup, not per-variant rows.
    variants: [],
    stock_summary: s.stockSummary ? mapStockSummary(s.stockSummary) : null,
    variant_count: s.variantCount,
    price_range: mapPriceRange(s.priceRange),
  };
}

/**
 * The gallery ids for a write, under the ALL-UUID-OR-OMIT rule: the full ordered id list
 * when EVERY image row has resolved to a CE media id, else `undefined` so the field is
 * omitted and the stored gallery is left untouched. `imageIds` is a full replace on the
 * engine, so sending a partially-resolved list (an upload still in flight, a row that
 * never resolved) would silently drop the unresolved images — omission cannot lose
 * anything. An empty list (every image removed) still returns `[]`: the explicit clear.
 *
 * @param images - The payload's gallery rows, in display order.
 *
 * @returns The complete id list, or `undefined` when any row lacks one.
 */
function galleryImageIdsForWrite(
  images: ShopProductPayload['images'],
): string[] | undefined {
  const ids = images.flatMap((img) => (img.id ? [img.id] : []));
  return ids.length === images.length ? ids : undefined;
}

/**
 * Builds the `/v1` create-product request from the form's write payload. Maps the
 * WooCommerce-style fields to the single-call aggregate: status/type, `Money`
 * (stamped with the shop currency), stock, weight (kg → grams), per-order limit,
 * categories, gallery image ids, and the variation axes (`options`). Empty optional
 * values are omitted so the `.strict()` contract accepts the body; an empty `sku`
 * is dropped (the contract requires a non-empty one). Gallery images are sent only
 * when they carry an uploaded media id; a `digital` product's existing download
 * files map to `download.mediaIds`.
 *
 * @param payload - The product write payload from the form.
 * @param currency - The shop's ISO-4217 currency, stamped on every `Money`.
 *
 * @returns The `/v1` create-product request body.
 */
export function buildCreateProductDto(
  payload: ShopProductPayload,
  currency: string,
): CreateProductRequestV1 {
  const isDigital = payload.virtual;
  const weight = parseDecimal(payload.weight);
  const options: ProductOptionInputV1[] = toVariationAxes(payload.attributes);
  const imageIds = galleryImageIdsForWrite(payload.images) ?? [];
  const downloadMediaIds = payload.downloads.map((d) => d.id).filter(Boolean);

  const dto: CreateProductRequestV1 = {
    name: payload.name,
    description: payload.description || null,
    shortDescription: payload.short_description || null,
    status: SHOP_TO_V1_STATUS[payload.status] ?? 'draft',
    productType: isDigital ? 'digital' : 'physical',
    price: toMoney(payload.regular_price, currency),
    salePrice: payload.sale_price ? toMoney(payload.sale_price, currency) : null,
    sku: payload.sku || undefined,
    maxPerOrder: payload.sold_individually ? 1 : null,
    weightGrams: !isDigital && weight !== null ? Math.round(weight * GRAMS_PER_KG) : null,
    trackInventory: payload.manage_stock,
    stockQuantity: payload.manage_stock ? (payload.stock_quantity ?? 0) : 0,
    categoryIds: payload.categories.map((c) => c.id),
    relatedProductIds: payload.cross_sell_ids,
  };
  if (options.length > 0) {
    dto.options = options;
  }
  if (imageIds.length > 0) {
    dto.imageIds = imageIds;
  }
  if (isDigital && downloadMediaIds.length > 0) {
    dto.download = { mediaIds: downloadMediaIds };
  }
  return dto;
}

/**
 * Builds the `/v1` update-product request body.
 *
 * Always full-replaces `sku` (empty → `null`). `imageIds` full-replaces the gallery only
 * when every row carries a resolved media id (all-uuid-or-omit — a partial list would
 * silently drop the unresolved images); otherwise the field is omitted and the stored
 * gallery is kept.
 *
 * `options` follows the CE PATCH contract (see `UpdateProductSchema` in contracts):
 * - **omit** `options` → engine takes the in-place path over the existing matrix.
 * - **send** `options` → engine regenerates the variant matrix; kept combinations keep
 *   their per-variant values (the in-place reconcile), top-level pricing seeds only NEW
 *   combinations, and `variants[]` overrides carry any per-combination edits.
 * - **send `options: []`** → drop all axes (product becomes simple).
 *
 * So `options` is included only when the form's axes differ from `previousAttributes`.
 *
 * **The flatten fix (options-absent path).** On the in-place path a product-level
 * `price`/`salePrice`/`stockQuantity`/`trackInventory`/`weightGrams` is applied UNIFORMLY
 * to every variant, overwriting per-variant overrides. For a VARIANT product those fields
 * are therefore STRIPPED here — the value-only edits that exist are routed to the batch
 * endpoint instead ({@link updateProductVariants}); nothing product-level is flattened over
 * the variants. A SIMPLE/digital product (genuinely one variant) keeps product-level pricing
 * in place, unchanged. On the `options`-present path pricing is always kept as the seed.
 *
 * @param payload - The product write payload from the form.
 * @param currency - The shop's ISO-4217 currency, stamped on every `Money`.
 * @param previousAttributes - Variation axes on the product before this edit.
 *
 * @returns The `/v1` update-product request body.
 */
export function buildUpdateProductDto(
  payload: ShopProductPayload,
  currency: string,
  previousAttributes: AttributeAxes = [],
): UpdateProductRequestV1 {
  const dto: UpdateProductRequestV1 = {
    ...buildCreateProductDto(payload, currency),
    sku: payload.sku || null,
  };
  // Full-replace the gallery only under the all-uuid-or-omit rule (see
  // {@link galleryImageIdsForWrite}): an omitted field leaves the stored gallery
  // untouched, where a partial list would silently drop the unresolved images.
  // `[]` is still sent — the explicit clear.
  const galleryIds = galleryImageIdsForWrite(payload.images);
  if (galleryIds !== undefined) {
    dto.imageIds = galleryIds;
  } else {
    delete dto.imageIds;
  }

  const nextAxes = toVariationAxes(payload.attributes);
  const previousAxes = toVariationAxes(previousAttributes);
  const isVariantProduct = payload.attributes.length > 0;

  if (variationAxesChanged(nextAxes, previousAxes)) {
    // Axes changed (or were cleared) — regenerate. `[]` drops all variants. Product-level
    // pricing stays as the seed for NEW combinations (`options`-present path).
    dto.options = nextAxes;
  } else {
    // Axes unchanged — omit so the engine takes the in-place path.
    // buildCreateProductDto may have set options; strip them for the in-place path.
    delete dto.options;
    if (isVariantProduct) {
      // The flatten fix: never apply product-level pricing uniformly over per-variant
      // overrides. Value-only edits go through the batch endpoint instead.
      delete dto.price;
      delete dto.salePrice;
      delete dto.stockQuantity;
      delete dto.trackInventory;
      delete dto.weightGrams;
    }
  }

  return dto;
}

// ---------------------------------------------------------------------------
// Per-variant write path — the batch-vs-aggregate dispatch (§ variants plan)
// ---------------------------------------------------------------------------

/** How the aggregate product write is shaped for a given save. */
export type AggregateWriteMode = 'create' | 'structural' | 'in-place';

/**
 * The write plan for a product save: which aggregate mode to use, whether product-level
 * pricing is stripped from the aggregate body (the flatten fix), and whether a follow-up
 * batch call carries the per-variant value edits.
 */
export interface WritePlan {
  aggregate: AggregateWriteMode;
  /** `true` when product-level pricing must NOT ride the aggregate (variant + in-place). */
  stripProductPricing: boolean;
  /** `true` when the dirty per-variant value edits go through the batch endpoint. */
  batch: boolean;
}

/**
 * Classifies a product save into its write plan — the single, explicit place the
 * batch-vs-aggregate dispatch is decided, because the failure mode is silent data loss.
 * The batch endpoint edits only existing variants on an UNCHANGED matrix and never touches
 * `options`; everything structural (create, add/remove/rename/reorder a value) goes through
 * the aggregate, whose `variants[]` overrides carry any per-combination edits. A variant
 * product on the in-place path strips product-level pricing so nothing is flattened over the
 * per-variant overrides; a simple product keeps it.
 *
 * @param input.isCreate - Whether this is the first save (aggregate `POST`).
 * @param input.axesChanged - Whether the variation matrix changed (or was cleared).
 * @param input.isVariantProduct - Whether the product has ≥1 variation axis.
 * @param input.hasDirtyVariantEdits - Whether any existing variant's value fields changed.
 *
 * @returns The {@link WritePlan}.
 */
export function chooseWritePath(input: {
  isCreate: boolean;
  axesChanged: boolean;
  isVariantProduct: boolean;
  hasDirtyVariantEdits: boolean;
}): WritePlan {
  if (input.isCreate) {
    return { aggregate: 'create', stripProductPricing: false, batch: false };
  }
  if (input.axesChanged) {
    // Structural edit — the aggregate PATCH restates per-combination edits via `variants[]`. `batch`
    // is false here because the matrix regenerate carries the value edits; but `updateProduct` still
    // runs a survivor-scoped retire flush afterwards (the override strips `enabled`), so a retire in
    // the same save isn't lost.
    return { aggregate: 'structural', stripProductPricing: false, batch: false };
  }
  if (input.isVariantProduct) {
    // Unchanged matrix on a variant product: product fields only via the aggregate (pricing
    // stripped), value-only edits via the batch endpoint.
    return { aggregate: 'in-place', stripProductPricing: true, batch: input.hasDirtyVariantEdits };
  }
  // Simple/digital product — keep product-level pricing on the in-place path (one variant).
  return { aggregate: 'in-place', stripProductPricing: false, batch: false };
}

/** Canonical kilogram string → integer grams (matches {@link buildCreateProductDto}). */
function weightToGrams(weight: string): number | null {
  const kg = parseDecimal(weight);
  return kg !== null ? Math.round(kg * GRAMS_PER_KG) : null;
}

/** The mutable per-variant wire fields shared by the batch item and the aggregate override. */
type VariantValueFields = Omit<VariantBatchItemV1, 'id' | 'enabled'>;

/** The full per-variant value set of a form row, as wire fields (used when there is no baseline). */
function fullVariantFields(row: ShopProductVariant, currency: string): VariantValueFields {
  return {
    price: toMoney(row.regular_price, currency),
    salePrice: row.sale_price ? toMoney(row.sale_price, currency) : null,
    sku: row.sku ? row.sku : null,
    trackInventory: row.manage_stock,
    stockQuantity: row.manage_stock ? (row.stock_quantity ?? 0) : 0,
    weightGrams: row.weight ? weightToGrams(row.weight) : null,
    imageId: row.image ? row.image.id : null,
  };
}

/**
 * The per-variant fields whose display value diverges from the baseline row, mapped to the
 * wire shape (`null` = clear, absent = leave alone). An UNCHANGED field is omitted — so an
 * untouched row yields `{}`, the core invariant that stops a batch wiping the other rows.
 * `enabled` (the retire flag) is included here and dropped for the aggregate override, which
 * cannot express it.
 *
 * @param row - The edited form row.
 * @param baseline - The pre-edit row (matched by id), or `undefined` for a new combination.
 * @param currency - The shop currency, stamped on money fields.
 *
 * @returns The changed value fields (plus `enabled` when it changed).
 */
export function diffVariantFields(
  row: ShopProductVariant,
  baseline: ShopProductVariant | undefined,
  currency: string,
): Partial<Omit<VariantBatchItemV1, 'id'>> {
  if (!baseline) {
    return fullVariantFields(row, currency);
  }
  const fields: Partial<Omit<VariantBatchItemV1, 'id'>> = {};
  if (row.regular_price !== baseline.regular_price) {
    fields.price = toMoney(row.regular_price, currency);
  }
  if (row.sale_price !== baseline.sale_price) {
    fields.salePrice = row.sale_price ? toMoney(row.sale_price, currency) : null;
  }
  if (row.sku !== baseline.sku) {
    fields.sku = row.sku ? row.sku : null;
  }
  if (row.weight !== baseline.weight) {
    fields.weightGrams = row.weight ? weightToGrams(row.weight) : null;
  }
  if (row.manage_stock !== baseline.manage_stock) {
    fields.trackInventory = row.manage_stock;
  }
  if (
    row.manage_stock &&
    (row.manage_stock !== baseline.manage_stock ||
      row.stock_quantity !== baseline.stock_quantity)
  ) {
    fields.stockQuantity = row.stock_quantity ?? 0;
  }
  if ((row.image?.id ?? null) !== (baseline.image?.id ?? null)) {
    fields.imageId = row.image ? row.image.id : null;
  }
  if (row.enabled !== baseline.enabled) {
    fields.enabled = row.enabled;
  }
  return fields;
}

/**
 * Builds a single batch item from a row's DIRTY-FIELD SET (never the whole row object). An
 * untouched row serialises to `{ id }` alone — `sku`/`salePrice`/`weightGrams`/`imageId`
 * clear on `null`, so serialising a full row would wipe those on every untouched variant.
 *
 * @param row - The edited form row.
 * @param baseline - The pre-edit row (matched by id).
 * @param currency - The shop currency.
 *
 * @returns The `{ id, ...changedFields }` batch item.
 */
export function buildVariantBatchItem(
  row: ShopProductVariant,
  baseline: ShopProductVariant | undefined,
  currency: string,
): VariantBatchItemV1 {
  return { id: row.id, ...diffVariantFields(row, baseline, currency) };
}

/**
 * Builds the batch body from the edited rows and the pre-edit baseline: one dirty-field item
 * per CHANGED existing variant, matched by id. Untouched rows (and rows with no baseline, i.e.
 * not-yet-created combinations) are omitted, so the batch touches only what changed.
 *
 * @param rows - The edited form rows.
 * @param baseline - The pre-edit variant rows (from the loaded product).
 * @param currency - The shop currency.
 *
 * @returns The changed batch items (empty when nothing per-variant changed).
 */
export function buildVariantBatchItems(
  rows: ShopProductVariant[],
  baseline: ShopProductVariant[],
  currency: string,
): VariantBatchItemV1[] {
  const baselineById = new Map(baseline.map((v) => [v.id, v]));
  return rows.flatMap((row) => {
    const base = baselineById.get(row.id);
    if (!base) {
      return [];
    }
    const item = buildVariantBatchItem(row, base, currency);
    // More than just `{ id }` means something actually changed.
    return Object.keys(item).length > 1 ? [item] : [];
  });
}

/** The display label of an axis value, whether it rides the wire as a bare string or `{ id, value }`. */
function axisValueLabel(value: string | { id?: string; value: string }): string {
  return typeof value === 'string' ? value : value.value;
}

/**
 * Case-insensitive label key, mirroring the engine's `fold` (`trim().toLowerCase()`). The engine
 * matches axes by folded name, falls back to a folded-label match for an id-less value, and keys
 * override selectors the same way (`overrideKey`) — so a pure recase is preserved in place, and an
 * override stated in the OLD casing still matches its combination.
 */
function foldLabel(label: string): string {
  return label.trim().toLowerCase();
}

/**
 * Whether a row's combination is representable under the given axes (survives a matrix edit).
 *
 * Compared on the FOLDED name/label, not the raw one. Renames are id-preserving in the option
 * editor (the pill's inline rename keeps the value id, and `relabelRowSelections` rewrites the
 * rows' selections to the new labels before this runs), so a renamed combination arrives here
 * already matching. The fold still matters for the id-LESS paths — a value removed and re-typed
 * (which the engine reclaims by folded label, including a pure case fix) — where a raw compare
 * would drop the row's override for a combination that in fact survives, silently losing a
 * per-variant edit made in the same save. A fold-CHANGING id-less relabel is a genuine removal
 * (the id is gone and the fold no longer matches), so it correctly reads as not surviving.
 */
function comboSurvives(
  row: ShopProductVariant,
  axes: ProductOptionInputV1[],
): boolean {
  return row.options.every((sel) =>
    axes.some(
      (axis) =>
        foldLabel(axis.name) === foldLabel(sel.name) &&
        axis.values.some((v) => foldLabel(axisValueLabel(v)) === foldLabel(sel.value)),
    ),
  );
}

/**
 * Builds the aggregate `variants[]` overrides for the structural (`options`-present) path.
 * Only rows whose per-variant values CHANGED are emitted (as subset overrides targeted by
 * `{ name, value }` selectors); untouched survivors keep their values via the in-place
 * reconcile, and new combinations inherit the product-level seed. `enabled` is not
 * expressible in an aggregate override, so a retire is left to the batch path. Rows whose
 * combination does not survive the new axes are skipped (their combo no longer exists).
 *
 * Emitting DIRTY-ONLY overrides is safe (not lossy): the CE `reconcileVariants` writes a
 * surviving combination ONLY when an override is present ("no override → no write at all"),
 * and an override is a PARTIAL patch (an omitted field keeps the survivor's current value,
 * not the product-level seed) — so an untouched survivor is never reset, and top-level pricing
 * seeds only genuinely NEW combinations. (Verified against `product.service.ts`
 * `reconcileVariants`; the frozen plan's "restate every survivor" is the pessimistic framing.)
 *
 * @param rows - The edited form rows.
 * @param baseline - The pre-edit variant rows (matched by id).
 * @param nextAxes - The variation axes being written.
 * @param currency - The shop currency.
 *
 * @returns The per-combination overrides (empty when no surviving row changed).
 */
export function buildVariantOverrides(
  rows: ShopProductVariant[],
  baseline: ShopProductVariant[],
  nextAxes: ProductOptionInputV1[],
  currency: string,
): VariantOverrideInputV1[] {
  const baselineById = new Map(baseline.map((v) => [v.id, v]));
  return rows.flatMap((row) => {
    if (!comboSurvives(row, nextAxes)) {
      return [];
    }
    const { enabled: _enabled, ...values } = diffVariantFields(
      row,
      baselineById.get(row.id),
      currency,
    );
    void _enabled;
    return Object.keys(values).length > 0
      ? [{ options: row.options, ...values }]
      : [];
  });
}

/**
 * Applies a batch of per-variant patches (`PATCH /v1/products/:id/variants`) in one
 * all-or-nothing transaction. The whole batch is rejected (422) if any id is unknown or
 * soft-deleted; a stale `updatedAt` token 409s. The caller batches every dirty row into ONE
 * call at save time (nothing debounces — there is no per-keystroke write path).
 *
 * @param id - The product id.
 * @param body - The batch body (`variants[]` dirty-field patches + optional `updatedAt`).
 *
 * @returns The updated variants, mapped to the design shape.
 *
 * @throws {WcApiError} On a rejected batch (422) or stale token (409).
 */
export async function updateProductVariants(
  id: string,
  body: BatchUpdateVariantsRequestV1,
): Promise<ShopProductVariant[]> {
  // The batch endpoint returns the WHOLE updated product (`toProductResponseV1`), NOT a bare
  // variant array — so map its `variants`, resolving each variant's option-value labels against
  // the product's own `options` (which the response carries).
  const updated = await v1Mutate<ProductResponseV1>(
    `/products/${id}/variants`,
    'PATCH',
    body,
  );
  return updated.variants.map((v) => toShopProductVariant(v, updated.options));
}

/**
 * Fetches a page of products with optional search, category, and sort.
 *
 * @param params - Pagination, search, category, and sort options.
 *
 * @returns The products page wrapped in a {@link WcResult} (with totals).
 *
 * @throws {WcApiError} When the request fails.
 */
export async function fetchProducts(
  params: FetchProductsParams = {},
): Promise<WcResult<ShopProduct[]>> {
  const {
    page = 1,
    perPage = PRODUCTS_PER_PAGE,
    search = '',
    status = '',
    category = '',
    availability,
    orderby,
    order,
  } = params;
  const qs = new URLSearchParams({ page: String(page), pageSize: String(perPage) });
  if (search) {
    qs.set('search', search);
  }
  // `/v1` `status` accepts a comma-separated set (matches ANY), so a multi-select maps each
  // WooCommerce-style status to its `/v1` lifecycle status; unknown tokens are dropped.
  const v1Statuses = status
    .split(',')
    .map((s) => SHOP_TO_V1_STATUS[s.trim()])
    .filter(Boolean);
  if (v1Statuses.length > 0) {
    qs.set('status', v1Statuses.join(','));
  }
  // `/v1` `categoryId` accepts the full comma-separated set (matches ANY of them), so the
  // multi-select category filter passes straight through (trimmed, empties dropped).
  const categoryIds = category
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);
  if (categoryIds.length > 0) {
    qs.set('categoryId', categoryIds.join(','));
  }
  // Server-side stock filter (fixes the page-1-only client filter). CE has no backorder.
  if (availability) {
    qs.set('availability', availability === 'instock' ? 'in_stock' : 'out_of_stock');
  }
  const sort =
    orderby === 'title'
      ? 'name'
      : orderby === 'price'
        ? 'price'
        : orderby === 'status'
          ? 'status'
          : orderby === 'stock'
            ? 'stock'
            : undefined;
  if (sort) {
    qs.set('sort', sort);
    if (order) {
      qs.set('order', order);
    }
  }
  const res = await v1Get<ProductSummaryResponseV1[]>(`/products?${qs.toString()}`);
  return {
    data: res.data.map(toShopProductSummary),
    total: res.total,
    totalPages: res.totalPages,
  };
}

/**
 * Fetches a single product by id, used to seed the edit/duplicate form on a
 * direct link or refresh (the list view passes its cached copy when present).
 *
 * @param id - The product id to fetch.
 *
 * @returns The product.
 *
 * @throws {WcApiError} When the request fails (e.g. 404 for an unknown id).
 */
export async function fetchProductById(id: string): Promise<ShopProduct> {
  const res = await v1Get<ProductResponseV1>(`/products/${id}`);
  return toShopProduct(res.data);
}

/**
 * Creates a new product against the `/v1` API. A create is always the aggregate `POST`;
 * per-variant overrides are sent only when the form materialised per-combination values on
 * the create form (otherwise the engine seeds every variant from the product-level price).
 *
 * @param payload - The product write payload.
 * @param variantRows - The per-combination values authored on the create form (usually empty
 *   — variants are typically materialised uniformly and then edited).
 *
 * @returns The created product, mapped to the `Shop*` shape.
 *
 * @throws {WcApiError} When the request fails (e.g. a currency or validation error).
 */
export async function createProduct(
  payload: ShopProductPayload,
  variantRows: ShopProductVariant[] = [],
): Promise<ShopProduct> {
  const currency = await getShopCurrency();
  const dto = buildCreateProductDto(payload, currency);
  if (payload.attributes.length > 0 && variantRows.length > 0) {
    const overrides = buildVariantOverrides(
      variantRows,
      [],
      toVariationAxes(payload.attributes),
      currency,
    );
    if (overrides.length > 0) {
      dto.variants = overrides;
    }
  }
  const created = await v1Mutate<ProductResponseV1>('/products', 'POST', dto);
  return toShopProduct(created);
}

/**
 * Updates an existing product against the `/v1` API, dispatching per {@link chooseWritePath}:
 * the aggregate `PATCH` carries the product fields (and, on a structural edit, `options` +
 * per-combination overrides); dirty per-variant value edits on an unchanged matrix go through
 * the {@link updateProductVariants} batch endpoint afterwards. After a batch the full product
 * is re-fetched so the returned rows reflect both writes.
 *
 * @param id - The product id to update.
 * @param payload - The product write payload.
 * @param previousAttributes - The axes on the product loaded into the form (pre-edit);
 *   used so unchanged `options` are omitted and the flatten fix applies.
 * @param variantRows - The current per-variant table rows (edited).
 * @param baseline - The pre-edit per-variant rows (from the loaded product), the diff source.
 *
 * @returns The updated product, mapped to the `Shop*` shape.
 *
 * @throws {WcApiError} When the request fails (e.g. a currency or validation error).
 */
export async function updateProduct(
  id: string,
  payload: ShopProductPayload,
  previousAttributes: AttributeAxes = [],
  variantRows: ShopProductVariant[] = [],
  baseline: ShopProductVariant[] = [],
  previousUpdatedAt?: string,
): Promise<ShopProduct> {
  const currency = await getShopCurrency();
  const nextAxes = toVariationAxes(payload.attributes);
  const axesChanged = variationAxesChanged(nextAxes, toVariationAxes(previousAttributes));
  const isVariantProduct = payload.attributes.length > 0;
  const batchItems = buildVariantBatchItems(variantRows, baseline, currency);
  const plan = chooseWritePath({
    isCreate: false,
    axesChanged,
    isVariantProduct,
    hasDirtyVariantEdits: batchItems.length > 0,
  });

  const dto = buildUpdateProductDto(payload, currency, previousAttributes);
  // Optimistic concurrency: send the `updatedAt` this editor loaded, so a product another
  // editor moved underneath 409s (`resource_stale`, mapped to a refetch message) instead
  // of being silently last-write-won. Token-less when the loaded product predates the
  // field (legacy read) — the engine then applies last-write-wins as before.
  if (previousUpdatedAt) {
    dto.updatedAt = previousUpdatedAt;
  }
  if (plan.aggregate === 'structural' && isVariantProduct) {
    const overrides = buildVariantOverrides(variantRows, baseline, nextAxes, currency);
    if (overrides.length > 0) {
      dto.variants = overrides;
    }
  }

  const updated = await v1Mutate<ProductResponseV1>(`/products/${id}`, 'PATCH', dto);
  if (plan.batch) {
    // Two-phase: the aggregate's fresh `updatedAt` is the batch's concurrency token, so the
    // second write is never a self-inflicted 409. Re-fetch for a product reflecting both.
    await updateProductVariants(id, { variants: batchItems, updatedAt: updated.updatedAt });
    return fetchProductById(id);
  }
  if (plan.aggregate === 'structural') {
    // A structural save regenerates the matrix via the aggregate override, which deliberately
    // STRIPS `enabled` (retire rides the batch endpoint only). So a retire applied on a SURVIVING
    // combination in the same save would otherwise be silently dropped. Flush those retire changes
    // through the batch, scoped to survivors — variants that still exist post-regenerate (their ids
    // are stable). New/removed combinations are excluded (a removed combo is already soft-deleted; a
    // new one has no server id). Only `enabled` is sent: the survivors' other value edits already
    // rode the override, so re-sending them would be redundant.
    const survivorIds = new Set(updated.variants.map((v) => v.id));
    const retireFlush = batchItems.flatMap((it) =>
      it.enabled !== undefined && survivorIds.has(it.id) ? [{ id: it.id, enabled: it.enabled }] : [],
    );
    if (retireFlush.length > 0) {
      await updateProductVariants(id, { variants: retireFlush, updatedAt: updated.updatedAt });
      return fetchProductById(id);
    }
  }
  return toShopProduct(updated);
}

/**
 * Soft-deletes a product (`DELETE /v1/products/:id`, returning 204).
 *
 * @param id - The product id to delete.
 *
 * @throws {WcApiError} When the request fails.
 */
export async function deleteProduct(id: string): Promise<void> {
  await v1Mutate(`/products/${id}`, 'DELETE');
}
